import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyBankWithdrawalAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyWithdrawalQueue = createAsyncThunk(
    'verifyBankWithdrawal/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationWithdrawals(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank withdrawal verification queue')); }
    }
);

export const fetchVerifyWithdrawalDetail = createAsyncThunk(
    'verifyBankWithdrawal/fetchDetail',
    async (bankTranId, { rejectWithValue }) => {
        try { return await api.getVerificationWithdrawalById(bankTranId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch withdrawal details')); }
    }
);

export const fetchReturnedWithdrawalDetail = createAsyncThunk(
    'verifyBankWithdrawal/fetchReturnedDetail',
    async (bankTranId, { rejectWithValue }) => {
        try { return await api.getReturnedWithdrawalById(bankTranId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch returned withdrawal details')); }
    }
);

export const fetchWithdrawalBanks = createAsyncThunk(
    'verifyBankWithdrawal/fetchBanks',
    async (_, { rejectWithValue }) => {
        try { return await api.getBanks(); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch banks')); }
    }
);

export const fetchWithdrawalCheques = createAsyncThunk(
    'verifyBankWithdrawal/fetchCheques',
    async (bankName, { rejectWithValue }) => {
        try { return await api.getChequeNos(bankName); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch cheque numbers')); }
    }
);

export const submitVerifyWithdrawal = createAsyncThunk(
    'verifyBankWithdrawal/verify',
    async (payload, { rejectWithValue }) => {
        try { return await api.verifyWithdrawal(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While WithDraw Verification')); }
    }
);

export const submitUpdateWithdrawal = createAsyncThunk(
    'verifyBankWithdrawal/update',
    async (payload, { rejectWithValue }) => {
        try { return await api.updateReturnedWithdrawal(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While Return Withdraw Updation')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    banks: [], cheques: [], chequesLoading: false,
    verifyLoading: false, updateLoading: false,
};

// The pending-detail endpoint returns a one-row list; the returned-detail endpoint a single object.
const firstRow = (data) => (Array.isArray(data) ? data[0] || null : data || null);

const verifyBankWithdrawalSlice = createSlice({
    name: 'verifyBankWithdrawal',
    initialState,
    reducers: {
        clearVerifyWithdrawalDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; s.cheques = []; },
        resetVerifyBankWithdrawal: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyWithdrawalQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyWithdrawalQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyWithdrawalQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        [fetchVerifyWithdrawalDetail, fetchReturnedWithdrawalDetail].forEach((thunk) => {
            builder
                .addCase(thunk.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.detailLoading = false; s.detail = firstRow(a.payload?.Data); })
                .addCase(thunk.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });
        });

        builder
            .addCase(fetchWithdrawalBanks.fulfilled, (s, a) => { s.banks = a.payload?.Data || []; })
            .addCase(fetchWithdrawalBanks.rejected, (s) => { s.banks = []; });

        builder
            .addCase(fetchWithdrawalCheques.pending, (s) => { s.chequesLoading = true; s.cheques = []; })
            .addCase(fetchWithdrawalCheques.fulfilled, (s, a) => { s.chequesLoading = false; s.cheques = a.payload?.Data || []; })
            .addCase(fetchWithdrawalCheques.rejected, (s) => { s.chequesLoading = false; s.cheques = []; });

        builder
            .addCase(submitVerifyWithdrawal.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitVerifyWithdrawal.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitVerifyWithdrawal.rejected, (s) => { s.verifyLoading = false; });

        builder
            .addCase(submitUpdateWithdrawal.pending, (s) => { s.updateLoading = true; })
            .addCase(submitUpdateWithdrawal.fulfilled, (s) => { s.updateLoading = false; })
            .addCase(submitUpdateWithdrawal.rejected, (s) => { s.updateLoading = false; });
    },
});

export const { clearVerifyWithdrawalDetail, resetVerifyBankWithdrawal } = verifyBankWithdrawalSlice.actions;

export const selectVBWQueue = (s) => s.verifyBankWithdrawal.queue;
export const selectVBWQueueLoading = (s) => s.verifyBankWithdrawal.queueLoading;
export const selectVBWQueueError = (s) => s.verifyBankWithdrawal.queueError;
export const selectVBWDetail = (s) => s.verifyBankWithdrawal.detail;
export const selectVBWDetailLoading = (s) => s.verifyBankWithdrawal.detailLoading;
export const selectVBWDetailError = (s) => s.verifyBankWithdrawal.detailError;
export const selectVBWBanks = (s) => s.verifyBankWithdrawal.banks;
export const selectVBWCheques = (s) => s.verifyBankWithdrawal.cheques;
export const selectVBWChequesLoading = (s) => s.verifyBankWithdrawal.chequesLoading;
export const selectVBWVerifyLoading = (s) => s.verifyBankWithdrawal.verifyLoading;
export const selectVBWUpdateLoading = (s) => s.verifyBankWithdrawal.updateLoading;

export default verifyBankWithdrawalSlice.reducer;
